// Frontend caching utility with localStorage + in-memory hybrid approach

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  staleWhileRevalidate?: boolean; // Allow stale data while revalidating
  storage?: 'memory' | 'localStorage' | 'both'; // Storage strategy
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const STALE_TIME = 30 * 1000; // 30 seconds - time after which data is considered stale

class CacheManager {
  private memoryCache: Map<string, CacheEntry<any>> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private refetchCallbacks: Map<string, () => Promise<any>> = new Map();

  /**
   * Get value from cache (memory first, then localStorage)
   */
  get<T>(key: string): T | null {
    // Check memory cache first
    const memoryCached = this.memoryCache.get(key);
    if (memoryCached && memoryCached.expiresAt > Date.now()) {
      return memoryCached.data as T;
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(`cache:${key}`);
      if (stored) {
        const entry: CacheEntry<T> = JSON.parse(stored);
        if (entry.expiresAt > Date.now()) {
          // Restore to memory cache
          this.memoryCache.set(key, entry);
          return entry.data;
        } else {
          // Remove expired entry
          localStorage.removeItem(`cache:${key}`);
        }
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return null;
  }

  /**
   * Get stale data from cache (even if expired)
   */
  getStale<T>(key: string): T | null {
    // Check memory cache
    const memoryCached = this.memoryCache.get(key);
    if (memoryCached) {
      return memoryCached.data as T;
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(`cache:${key}`);
      if (stored) {
        const entry: CacheEntry<T> = JSON.parse(stored);
        return entry.data;
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return null;
  }

  /**
   * Check if cache entry is stale (but not expired)
   */
  isStale(key: string): boolean {
    const memoryCached = this.memoryCache.get(key);
    if (memoryCached) {
      const age = Date.now() - memoryCached.timestamp;
      return age > STALE_TIME && memoryCached.expiresAt > Date.now();
    }

    try {
      const stored = localStorage.getItem(`cache:${key}`);
      if (stored) {
        const entry: CacheEntry<any> = JSON.parse(stored);
        const age = Date.now() - entry.timestamp;
        return age > STALE_TIME && entry.expiresAt > Date.now();
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return false;
  }

  /**
   * Set value in cache
   */
  set<T>(key: string, data: T, config?: CacheConfig): void {
    const ttl = config?.ttl || DEFAULT_TTL;
    const storage = config?.storage || 'both';
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    };

    // Store in memory
    if (storage === 'memory' || storage === 'both') {
      this.memoryCache.set(key, entry);
    }

    // Store in localStorage
    if (storage === 'localStorage' || storage === 'both') {
      try {
        localStorage.setItem(`cache:${key}`, JSON.stringify(entry));
      } catch (error) {
        console.warn('Failed to write to localStorage:', error);
      }
    }
  }

  /**
   * Delete value from cache
   */
  delete(key: string): void {
    this.memoryCache.delete(key);
    try {
      localStorage.removeItem(`cache:${key}`);
    } catch (error) {
      console.warn('Failed to delete from localStorage:', error);
    }
  }

  /**
   * Clear all cache including ETags and refetch callbacks
   */
  clear(): void {
    this.memoryCache.clear();
    this.refetchCallbacks.clear();
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('cache:') || key.startsWith('etag:')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  /**
   * Invalidate cache entries and ETags matching pattern
   * Also triggers background refetch for any active keys
   */
  invalidatePattern(pattern: string): void {
    // Clear memory cache and trigger refetch for matching keys
    const memoryKeys = Array.from(this.memoryCache.keys());
    const keysToRefetch: string[] = [];

    memoryKeys.forEach(key => {
      if (key.includes(pattern)) {
        this.memoryCache.delete(key);
        // Track keys that have active refetch callbacks
        if (this.refetchCallbacks.has(key)) {
          keysToRefetch.push(key);
        }
      }
    });

    // Clear localStorage cache and ETags
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if ((key.startsWith('cache:') || key.startsWith('etag:')) && key.includes(pattern)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to invalidate localStorage:', error);
    }

    // Trigger background refetch for active keys after a short delay
    // This allows backend cache to clear and process the mutation
    if (keysToRefetch.length > 0) {
      setTimeout(() => {
        keysToRefetch.forEach(key => {
          const refetchFn = this.refetchCallbacks.get(key);
          if (refetchFn) {
            // Refetch in background without waiting
            refetchFn().catch(err => {
              console.error(`Background refetch failed for ${key}:`, err);
            });
          }
        });
      }, 100); // 100ms delay to allow backend processing
    }
  }

  /**
   * Fetch with cache and request deduplication
   * Also registers the fetch function for automatic background refetch on invalidation
   */
  async fetchWithCache<T>(
    key: string,
    fetchFn: () => Promise<T>,
    config?: CacheConfig
  ): Promise<T> {
    const staleWhileRevalidate = config?.staleWhileRevalidate ?? true;

    // Register fetch function for potential background refetch
    this.refetchCallbacks.set(key, async () => {
      const data = await fetchFn();
      this.set(key, data, config);
      return data;
    });

    // Check if there's a pending request for this key
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending;
    }

    // Check cache
    const cached = this.get<T>(key);
    if (cached && !this.isStale(key)) {
      return cached;
    }

    // Stale-while-revalidate: return stale data and revalidate in background
    if (staleWhileRevalidate && cached) {
      // Return stale data immediately
      const staleData = cached;

      // Revalidate in background
      this.revalidate(key, fetchFn, config).catch(err => {
        console.error('Background revalidation failed:', err);
      });

      return staleData;
    }

    // Fetch fresh data
    const promise = fetchFn().then(data => {
      this.set(key, data, config);
      this.pendingRequests.delete(key);
      return data;
    }).catch(err => {
      this.pendingRequests.delete(key);
      // If fetch fails and we have stale data, return it
      const staleData = this.getStale<T>(key);
      if (staleData) {
        return staleData;
      }
      throw err;
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Revalidate cache entry in background
   */
  private async revalidate<T>(
    key: string,
    fetchFn: () => Promise<T>,
    config?: CacheConfig
  ): Promise<void> {
    try {
      const data = await fetchFn();
      this.set(key, data, config);
    } catch (error) {
      console.error('Revalidation failed:', error);
    }
  }

  /**
   * Generate cache key from parts
   */
  generateKey(...parts: (string | number | undefined | null)[]): string {
    return parts.filter(p => p !== undefined && p !== null).join(':');
  }

  /**
   * Prefetch data and store in cache
   */
  async prefetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    config?: CacheConfig
  ): Promise<void> {
    // Don't prefetch if already cached and not stale
    if (this.get(key) && !this.isStale(key)) {
      return;
    }

    try {
      const data = await fetchFn();
      this.set(key, data, config);
    } catch (error) {
      console.error('Prefetch failed:', error);
    }
  }
}

export const cacheManager = new CacheManager();

// Predefined TTL configurations
export const CACHE_TTL = {
  SHORT: 30 * 1000,           // 30 seconds
  MEDIUM: 5 * 60 * 1000,      // 5 minutes
  LONG: 15 * 60 * 1000,       // 15 minutes
  EXTRA_LONG: 60 * 60 * 1000, // 1 hour
};



